import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Button
} from '@mui/material';
import {
  Send,
  AttachFile,
  Close,
  Image as ImageIcon,
  CheckCircle,
  LocalShipping,
  ShoppingCart,
  Receipt,
  Person,
  History
} from '@mui/icons-material';
import { chatService } from '../services/chatService';
import { orderService } from '../services/orderService';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import OrderSummaryForm from './OrderSummaryForm';

const ChatWindow = ({ conversation, pharmacy, customer, onClose, onOrderPlaced }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [order, setOrder] = useState(null);
  const [rider, setRider] = useState(null);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const receiptInputRef = useRef(null);
  
  const { socket } = useSocket();
  const { user } = useAuth();

  // Determine conversation type - rider-customer or customer-pharmacy
  const conversationType = conversation?.conversationType || 'customer-pharmacy';
  const isRiderConversation = conversationType === 'rider-customer' || conversation?.riderId;
  
  // Get the other party (rider or pharmacy) for the header
  const otherParty = isRiderConversation 
    ? (customer || conversation?.customerId || conversation?.riderId) 
    : (pharmacy || conversation?.pharmacyId);

  // Load messages, order status and mark as read
  useEffect(() => {
    if (conversation) {
      loadMessages();
      markMessagesAsRead();
      loadOrderStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation]);

  // Load order status function
  const loadOrderStatus = useCallback(async () => {
    try {
      const data = await chatService.getOrderStatusFromChat(conversation._id);
      setOrder(data.order);
      
      // Load rider details if order has rider
      if (data.order.riderId) {
        loadRiderDetails(data.order.riderId);
      }
    } catch (err) {
      // Order might not exist yet, that's okay
      console.log('No order found for this conversation');
    }
  }, [conversation._id]);

  // Handler functions for socket events
  const handlePaymentConfirmed = useCallback((data) => {
    if (data.conversationId === conversation._id) {
      loadOrderStatus();
      showNotification('Payment Confirmed!', `Your payment for order #${data.orderNumber} has been confirmed. Your order is being prepared.`);
    }
  }, [conversation._id, loadOrderStatus]);

  const handleOrderStatusUpdate = useCallback((data) => {
    if (data.conversationId === conversation._id) {
      loadOrderStatus();
      showNotification('Order Status Update', `Your order status has been updated to: ${data.status}`);
    }
  }, [conversation._id, loadOrderStatus]);

  const handleNewMessage = useCallback((data) => {
    if (data.conversationId === conversation._id) {
      setMessages(prev => {
        // Check if message already exists (avoid duplicates)
        const messageExists = prev.some(msg => 
          msg._id === data.message._id || 
          (msg.senderId === data.message.senderId && 
           msg.createdAt === data.message.createdAt)
        );
        
        if (messageExists) {
          return prev;
        }
        
        return [...prev, data.message];
      });
    }
  }, [conversation._id]);

  // Join conversation room for real-time updates
  useEffect(() => {
    if (socket && conversation) {
      socket.emit('join-conversation', conversation._id);
      
      // Listen for new messages
      socket.on('new-message', handleNewMessage);
      
      // Listen for order updates
      socket.on('payment-confirmed', handlePaymentConfirmed);
      socket.on('order-status-update', handleOrderStatusUpdate);
      
      return () => {
        socket.emit('leave-conversation', conversation._id);
        socket.off('new-message', handleNewMessage);
        socket.off('payment-confirmed', handlePaymentConfirmed);
        socket.off('order-status-update', handleOrderStatusUpdate);
      };
    }
  }, [socket, conversation, handleNewMessage, handleOrderStatusUpdate, handlePaymentConfirmed]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatService.getMessages(conversation._id);
      setMessages(data.messages || []);
      setError(null);
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await chatService.markAsRead(conversation._id);
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  };


  const loadRiderDetails = async (riderId) => {
    try {
      // You can implement this by calling your rider service
      // For now, we'll just store the rider ID
      setRider({ _id: riderId, name: 'Rider', phone: 'N/A' });
    } catch (err) {
      console.error('Failed to load rider details:', err);
    }
  };


  const showNotification = (title, body) => {
    // Check if browser supports notifications
    if ('Notification' in window) {
      // Request permission if not granted
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: '/logo192.png'
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body: body,
              icon: '/logo192.png'
            });
          }
        });
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !imagePreview) return;

    try {
      setSending(true);
      setError(null);

      // Create optimistic message
      const optimisticMessage = {
        senderId: user.id,
        senderType: 'customer',
        content: imagePreview ? null : newMessage,
        imageUrl: imagePreview ? URL.createObjectURL(imagePreview) : null,
        isRead: false,
        createdAt: new Date().toISOString()
      };

      // Add message to local state immediately for instant feedback
      setMessages(prev => [...prev, optimisticMessage]);
      setNewMessage('');
      
      if (imagePreview) {
        // Send image
        await chatService.sendImage(conversation._id, imagePreview);
        setImagePreview(null);
      } else {
        // Send text message
        await chatService.sendMessage(conversation._id, newMessage);
      }

      // Reload messages to get the actual message from server
      await loadMessages();
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImagePreview(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendOrderSummary = () => {
    setShowOrderSummary(true);
  };

  const handleCloseOrderSummary = () => {
    setShowOrderSummary(false);
  };

  const handleCreateOrder = async (orderData) => {
    try {
      setSending(true);
      setError(null);
      
      const response = await chatService.createOrderFromChat(conversation._id, orderData);
      
      // Reload order status to show the new order
      await loadOrderStatus();
      
      // Send a message to customer about the order
      await chatService.sendMessage(
        conversation._id,
        `Order created! Order #${response.order.orderNumber}. Total: ₦${response.order.totalAmount.toLocaleString()}. Please review and proceed with payment.`
      );
      
      setShowOrderSummary(false);
      
      if (onOrderPlaced) {
        onOrderPlaced();
      }
    } catch (err) {
      setError('Failed to create order. Please try again.');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleUploadReceipt = () => {
    receiptInputRef.current?.click();
  };

  const handleReceiptSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setError('Please select an image or PDF file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      if (!order || !order._id) {
        setError('No order found to upload receipt for');
        return;
      }

      try {
        setSending(true);
        // Upload receipt to order
        await orderService.uploadPaymentReceipt(order._id, file);
        
        // Send a message to pharmacy about receipt upload
        await chatService.sendMessage(
          conversation._id,
          `Payment receipt uploaded for order #${order.orderNumber || order._id.slice(-8)}`
        );
        
        // Reload order status to get updated payment status
        await loadOrderStatus();
        
        setError(null);
      } catch (err) {
        setError('Failed to upload receipt');
        console.error(err);
      } finally {
        setSending(false);
        if (receiptInputRef.current) {
          receiptInputRef.current.value = '';
        }
      }
    }
  };

  const handleViewOrderHistory = () => {
    if (order && order._id) {
      window.open(`/customer/orders/${order._id}`, '_blank');
    }
  };

  const handleConfirmPayment = async () => {
    if (!order || !order._id) return;
    
    try {
      setSending(true);
      setError(null);
      
      await chatService.confirmPaymentFromChat(conversation._id, order._id);
      
      // Reload order status
      await loadOrderStatus();
      
      // Send message to customer
      await chatService.sendMessage(
        conversation._id,
        `Payment confirmed! Your order is being prepared. Thank you for your payment.`
      );
    } catch (err) {
      setError('Failed to confirm payment');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const getSenderName = (message) => {
    if (message.senderType === 'customer') {
      if (isRiderConversation) {
        // In rider-customer conversations, customer is the "other party" for riders
        return otherParty ? `${otherParty.firstName || ''} ${otherParty.lastName || ''}`.trim() || 'Customer' : 'Customer';
      }
      // In pharmacy conversations, show customer as current user
      return user.firstName || 'Customer';
    }
    if (message.senderType === 'rider') {
      // In rider-customer conversations, rider is the "other party" for customers
      return conversation?.riderId ? `${conversation.riderId.firstName || ''} ${conversation.riderId.lastName || ''}`.trim() || 'Rider' : 'Rider';
    }
    // Pharmacy message
    return pharmacy?.pharmacyName || conversation?.pharmacyId?.pharmacyName || 'Pharmacy';
  };

  const isMyMessage = (message) => {
    return message.senderId === user.id;
  };

  const getOrderStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ShoppingCart />;
      case 'confirmed':
        return <CheckCircle />;
      case 'preparing':
        return <ShoppingCart />;
      case 'ready':
        return <CheckCircle />;
      case 'assigned':
        return <LocalShipping />;
      case 'picked-up':
        return <LocalShipping />;
      case 'in-transit':
        return <LocalShipping />;
      case 'delivered':
        return <CheckCircle />;
      default:
        return <ShoppingCart />;
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        right: { xs: 0, sm: 20 },
        bottom: { xs: 0, sm: 20 },
        left: { xs: 0, sm: 'auto' },
        top: { xs: 0, sm: 'auto' },
        width: { xs: '100vw', sm: 400 },
        height: { xs: '100vh', sm: 600 },
        maxHeight: { xs: '100vh', sm: '80vh' },
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
        borderRadius: { xs: 0, sm: 1 }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }}>
            {isRiderConversation 
              ? (otherParty?.firstName?.charAt(0) || otherParty?.name?.charAt(0) || 'U')
              : (otherParty?.pharmacyName?.charAt(0) || 'P')
            }
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {isRiderConversation 
                ? (otherParty ? `${otherParty.firstName || ''} ${otherParty.lastName || ''}`.trim() || otherParty.name || 'User' : 'Rider/Customer')
                : (otherParty?.pharmacyName || 'Pharmacy')
              }
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {isRiderConversation ? 'Delivery Chat' : (conversation?.status === 'active' ? 'Online' : 'Offline')}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Order Status Bar */}
      {order && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getOrderStatusIcon(order.status)}
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Order #{order.orderNumber}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {order.status.replace('-', ' ').toUpperCase()}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`₦${order.totalAmount?.toLocaleString() || '0'}`}
              size="small"
              sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }}
            />
          </Box>
          
          {/* Rider Details */}
          {rider && order.status !== 'pending' && order.status !== 'confirmed' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Person fontSize="small" />
              <Typography variant="caption">
                Rider: {rider.name || 'Assigned'} • {rider.phone || 'N/A'}
              </Typography>
            </Box>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <IconButton
              size="small"
              onClick={handleViewOrderHistory}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
              title="View Order Details"
            >
              <History fontSize="small" />
            </IconButton>
            {order.paymentStatus === 'pending' && user.userType === 'customer' && (
              <IconButton
                size="small"
                onClick={handleUploadReceipt}
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                title="Upload Payment Receipt"
              >
                <Receipt fontSize="small" />
              </IconButton>
            )}
            {order.paymentStatus === 'pending' && user.userType === 'pharmacy' && (
              <Button
                size="small"
                variant="contained"
                onClick={handleConfirmPayment}
                sx={{ 
                  bgcolor: 'success.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'success.dark' }
                }}
                startIcon={<CheckCircle />}
              >
                Confirm Payment
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 1, sm: 2 },
          bgcolor: 'grey.50'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 4 }}
          >
            No messages yet. Start the conversation!
          </Typography>
        ) : (
          <>
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: isMyMessage(message) ? 'flex-end' : 'flex-start',
                  mb: { xs: 1.5, sm: 2 }
                }}
              >
                <Box
                  sx={{
                    maxWidth: { xs: '85%', sm: '70%' },
                    bgcolor: isMyMessage(message) ? 'primary.main' : 'white',
                    color: isMyMessage(message) ? 'white' : 'text.primary',
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    boxShadow: 1
                  }}
                >
                  {!isMyMessage(message) && (
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                      {getSenderName(message)}
                    </Typography>
                  )}
                  {message.content && (
                    <Typography variant="body2">{message.content}</Typography>
                  )}
                  {message.imageUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${message.imageUrl}`}
                        alt="Shared"
                        style={{
                          maxWidth: '100%',
                          borderRadius: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${message.imageUrl}`, '_blank')}
                      />
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.7,
                      display: 'block',
                      mt: 0.5,
                      textAlign: 'right'
                    }}
                  >
                    {new Date(message.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Image Preview */}
      {imagePreview && (
        <Box sx={{ p: 1, bgcolor: 'grey.100' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {imagePreview.name}
            </Typography>
            <IconButton size="small" onClick={handleRemoveImage}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Input Area */}
      <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
        {/* Pharmacy: Send Order Summary Button */}
        {user?.userType === 'pharmacy' && (
          <Box sx={{ mb: 1 }}>
            {order && (
              <Box sx={{ 
                p: 1, 
                bgcolor: 'info.light', 
                borderRadius: 1, 
                border: '1px solid', 
                borderColor: 'info.main',
                mb: 1
              }}>
                <Typography variant="caption" color="info.dark" display="block">
                  Latest Order #{order.orderNumber || order._id?.slice(-8)}
                </Typography>
                <Typography variant="caption" color="info.dark" display="block">
                  Status: {order.status?.replace('-', ' ').toUpperCase()}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<ShoppingCart />}
              onClick={handleSendOrderSummary}
              disabled={sending}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { 
                  bgcolor: 'primary.dark' 
                },
                borderRadius: 2,
                px: 2,
                py: 1,
                position: 'relative',
                zIndex: 1,
                cursor: 'pointer'
              }}
            >
              {order ? 'Create Additional Order' : 'Send Order Summary'}
            </Button>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*,.pdf"
            ref={receiptInputRef}
            onChange={handleReceiptSelect}
            style={{ display: 'none' }}
          />
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title="Upload Image"
          >
            <AttachFile />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={sending || (!newMessage.trim() && !imagePreview)}
          >
            {sending ? <CircularProgress size={20} /> : <Send />}
          </IconButton>
        </Box>
      </Box>

      {/* Order Summary Form */}
      <OrderSummaryForm
        open={showOrderSummary}
        onClose={handleCloseOrderSummary}
        onConfirm={handleCreateOrder}
        conversationId={conversation?._id}
      />
    </Paper>
  );
};

export default ChatWindow;

